import * as Sentry from '@sentry/react'
import mixpanel from 'mixpanel-browser'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './index.css'
// Package-level focus-visible + forced-colors baseline. Required: see
// packages/testops-command/README.md for the host contract.
import '@ytest/testops-command/styles.css'
import { initThemeFromStorage } from './hooks/useTheme'

// Apply persisted theme to <html> BEFORE React renders, so the initial
// paint already has the correct class. Without this, dark-mode users see
// a flash of light theme on hard reload while React boots.
initThemeFromStorage()

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 0.5,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  })
}

// Route Mixpanel events through ytest.ai/mp first-party proxy (see nginx
// /mp config + PRODUCTION_SAFETY §10d). Direct mixpanel.com requests get
// blocked by ad blockers; the proxy avoids that. Mirrors apps/athena/web/src/main.tsx.
if (import.meta.env.PROD && MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    track_pageview: true,
    persistence: 'localStorage',
    api_host: 'https://ytest.ai/mp',
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number }; status?: number })?.response?.status
                      ?? (error as { status?: number })?.status
        if (status === 401) return false
        return failureCount < 1
      },
      refetchOnWindowFocus: false,
    },
  },
})

const SentryErrorFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui' }}>
    <div style={{ textAlign: 'center', maxWidth: 400 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Something went wrong</h2>
      <p style={{ color: '#6b7280', marginBottom: 16 }}>An unexpected error occurred. Our team has been notified.</p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '8px 20px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
      >
        Reload Page
      </button>
    </div>
  </div>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>,
)
